import { db, storage } from "data/firebase";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  runTransaction,
  increment,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  orderBy,
  query,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { v4 as uuidv4 } from "uuid";

const COLLECTION_NAME = "legends";

export async function uploadFile(file, folder) {
  const fileExtension = file.name.split(".").pop();
  const fileName = `${uuidv4()}.${fileExtension}`;
  const storageRef = ref(storage, `${folder}/${fileName}`);

  await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(storageRef);
  return downloadURL;
}

export async function deleteFile(fileUrl) {
  if (!fileUrl) return;
  try {
    const storageRef = ref(storage, fileUrl);
    await deleteObject(storageRef);
  } catch (error) {
    console.warn("File deletion failed:", error);
  }
}

export const legendsRepo = {
  async list() {
    try {
      const q = query(collection(db, COLLECTION_NAME), orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error fetching legends:", error);
      return [];
    }
  },

  async getById(id) {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error("Error fetching legend:", error);
      return null;
    }
  },

  async create(data, files = {}) {
    try {
      const legendData = {
        ...data,
        imageUrl: data.imageUrl || null,
        audioUrl: data.audioUrl || null,
        videoUrl: data.videoUrl || null,
        isActive: typeof data.isActive === "boolean" ? data.isActive : true,
        approved: typeof data.approved === "boolean" ? data.approved : false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      if (files.image) {
        legendData.imageUrl = await uploadFile(files.image, "legends/images");
      }
      if (files.audio) {
        legendData.audioUrl = await uploadFile(files.audio, "legends/audio");
      }
      if (files.video) {
        legendData.videoUrl = await uploadFile(files.video, "legends/videos");
      }

      const docRef = await addDoc(collection(db, COLLECTION_NAME), legendData);
      return { id: docRef.id, ...legendData };
    } catch (error) {
      console.error("Error creating legend:", error);
      throw error;
    }
  },

  async hide(id, hiddenBy = {}) {
    if (!id) return;
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await updateDoc(docRef, {
        isActive: false,
        hiddenAt: serverTimestamp(),
        hiddenBy: hiddenBy || null,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error hiding legend:", error);
      throw error;
    }
  },

  async update(id, data, files = {}, oldUrls = {}) {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const updateData = {
        ...data,
        updatedAt: serverTimestamp(),
      };

      if (files.image) {
        if (oldUrls.imageUrl) await deleteFile(oldUrls.imageUrl);
        updateData.imageUrl = await uploadFile(files.image, "legends/images");
      }

      if (files.audio) {
        if (oldUrls.audioUrl) await deleteFile(oldUrls.audioUrl);
        updateData.audioUrl = await uploadFile(files.audio, "legends/audio");
      }

      if (files.video) {
        if (oldUrls.videoUrl) await deleteFile(oldUrls.videoUrl);
        updateData.videoUrl = await uploadFile(files.video, "legends/videos");
      }

      await updateDoc(docRef, updateData);
      return { id, ...updateData };
    } catch (error) {
      console.error("Error updating legend:", error);
      throw error;
    }
  },

  async remove(id, urls = {}) {
    try {
      if (urls.imageUrl)
        await deleteFile(urls.imageUrl);
      if (urls.audioUrl) await deleteFile(urls.audioUrl);
      if (urls.videoUrl)
        await deleteFile(urls.videoUrl);

      await deleteDoc(doc(db, COLLECTION_NAME, id));
    } catch (error) {
      console.error("Error deleting legend:", error);
      throw error;
    }
  },

  async toggleActive(id, isActive) {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await updateDoc(docRef, {
        isActive: isActive,
        updatedAt: serverTimestamp(),
      });
      return { id, isActive };
    } catch (error) {
      console.error("Error toggling legend status:", error);
      throw error;
    }
  },

  async incrementViewCount(id) {
    if (!id) return;
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await updateDoc(docRef, {
        viewCount: increment(1),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error incrementing viewCount:", error);
    }
  },

  async toggleLike(id, uid) {
    const safeId = String(id || "").trim();
    const safeUid = String(uid || "").trim();
    if (!safeId || !safeUid) {
      throw new Error("toggleLike requires legend id and uid");
    }

    const docRef = doc(db, COLLECTION_NAME, safeId);
    return runTransaction(db, async (transaction) => {
      const snap = await transaction.get(docRef);
      if (!snap.exists()) throw new Error("Legend not found");

      const data = snap.data() || {};
      const likedBy = Array.isArray(data.likedBy) ? data.likedBy : [];
      const alreadyLiked = likedBy.includes(safeUid);
      const nextLiked = !alreadyLiked;

      let likeCount = Number(data.likeCount);
      if (!Number.isFinite(likeCount) || likeCount < 0) {
        likeCount = likedBy.length;
      }

      const nextLikeCount = Math.max(0, likeCount + (nextLiked ? 1 : -1));

      transaction.update(docRef, {
        likeCount: nextLikeCount,
        likedBy: nextLiked ? arrayUnion(safeUid) : arrayRemove(safeUid),
        updatedAt: serverTimestamp(),
      });

      return { liked: nextLiked, likeCount: nextLikeCount };
    });
  },

  async like(id, uid) {
    const safeId = String(id || "").trim();
    const safeUid = String(uid || "").trim();
    if (!safeId || !safeUid) {
      throw new Error("like requires legend id and uid");
    }

    const docRef = doc(db, COLLECTION_NAME, safeId);
    return runTransaction(db, async (transaction) => {
      const snap = await transaction.get(docRef);
      if (!snap.exists()) throw new Error("Legend not found");

      const data = snap.data() || {};
      const likedBy = Array.isArray(data.likedBy) ? data.likedBy : [];
      const alreadyLiked = likedBy.includes(safeUid);

      let likeCount = Number(data.likeCount);
      if (!Number.isFinite(likeCount) || likeCount < 0) {
        likeCount = likedBy.length;
      }

      if (alreadyLiked) {
        return { liked: true, likeCount: Math.max(0, likeCount) };
      }

      const nextLikeCount = Math.max(0, likeCount + 1);
      transaction.update(docRef, {
        likeCount: nextLikeCount,
        likedBy: arrayUnion(safeUid),
        updatedAt: serverTimestamp(),
      });

      return { liked: true, likeCount: nextLikeCount };
    });
  },

  async likeAnonymous(id) {
    const safeId = String(id || "").trim();
    if (!safeId) throw new Error("likeAnonymous requires legend id");

    const docRef = doc(db, COLLECTION_NAME, safeId);
    return runTransaction(db, async (transaction) => {
      const snap = await transaction.get(docRef);
      if (!snap.exists()) throw new Error("Legend not found");

      const data = snap.data() || {};
      let likeCount = Number(data.likeCount);
      if (!Number.isFinite(likeCount) || likeCount < 0) {
        likeCount = 0;
      }

      const nextLikeCount = Math.max(0, likeCount + 1);
      transaction.update(docRef, {
        likeCount: nextLikeCount,
        updatedAt: serverTimestamp(),
      });

      return { likeCount: nextLikeCount };
    });
  },
};
